import dayjs from 'dayjs'
import { Calendar, CalendarDays, ListFilter } from 'lucide-react'
import { useEffect, useState, type HtmlHTMLAttributes } from 'react'

import { Button } from '@/modules/shared/components/ui/button'
import { Form } from '@/modules/shared/components/ui/form'
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetHeader,
	SheetTrigger,
} from '@/modules/shared/components/ui/sheet'
import { cn } from '@/modules/shared/lib/utils'

import { OrderFilterBadge } from './order-badge.component'
import { OrderFilterFormCodeComponent } from './order-filter-form-code.component'
import { OrderFilterFormPeriodComponent } from './order-filter-form-period.component'
import { OrderFilterTypeSelectorComponent } from './order-filter-type-selector.companent'
import { useFilterOrders } from './use-filter-orders.hook'

type TFilterSearchComponentProps = HtmlHTMLAttributes<HTMLDivElement>

export function OrderFilterSearchComponent({
	className,
	...rest
}: TFilterSearchComponentProps) {
	const {
		form,
		handleCancel,
		handleChangeFilterType,
		handleFormSubmit,
		handleRemoveFilter,
		filters,
		setShowTodayOrders,
		showTodayOrders,
		filterType,
	} = useFilterOrders()

	const [isSubmitting, setIsSubmitting] = useState(false)

	const handlePressEnter = async (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			setIsSubmitting(true)
			try {
				await handleFormSubmit(form.getValues())
			} finally {
				setIsSubmitting(false)
			}
		}
	}

	const handleSubmit = async (data: any) => {
		setIsSubmitting(true)
		try {
			await handleFormSubmit(data)
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<>
			<div className={cn('flex', className)} {...rest}>
				<div className="flex w-full gap-3 flex-wrap items-end">
					{filters.dataInicio && filters.dataFim && (
						<OrderFilterBadge
							label="Período"
							value={`${dayjs(filters.dataInicio).format('DD/MM/YYYY')} até ${dayjs(filters.dataFim).format('DD/MM/YYYY')}`}
							onRemove={() => handleRemoveFilter('data')}
							canRemove={!filters.cliente && !filters.status}
						/>
					)}
					{filters.cliente && (
						<OrderFilterBadge
							label="Cliente"
							value={filters.cliente}
							onRemove={() => handleRemoveFilter('cliente')}
						/>
					)}
					{filters.codigo && (
						<OrderFilterBadge
							label="Código"
							value={filters.codigo}
							onRemove={() => handleRemoveFilter('codigo')}
						/>
					)}
					{
						filters.cnpjcpf && (
							<OrderFilterBadge
								label="CNPJ/CPF"
								value={filters.cnpjcpf}
								onRemove={() => handleRemoveFilter('cnpjcpf')}
							/>
						)
					}
					{filters.status && (
						<OrderFilterBadge
							label="Status"
							value={filters.status}
							onRemove={() => handleRemoveFilter('status')}
						/>
					)}
					{!filters.dataInicio &&
						!filters.dataFim &&
						!filters.cliente &&
						!filters.codigo &&
						!filters.cnpjcpf &&
						!filters.status && (
							<div className="inline-flex rounded-lg border border-gray-200 p-1 bg-white shadow-xs">
								{/* Alterando para um toggle correto */}
								<div
									onClick={() => setShowTodayOrders(true)}
									onKeyDown={e => {
										if (e.key === 'Enter' || e.key === ' ') {
											setShowTodayOrders(true)
										}
									}}
									tabIndex={0}
									role="button"
									className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 cursor-pointer
		  ${showTodayOrders
											? 'bg-gray-200 text-gray-500'
											: 'bg-transparent text-gray-500 hover:bg-gray-50'
										}`}
								>
									<Calendar className="w-4 h-4" />
									Hoje
								</div>
								<div
									onClick={() => setShowTodayOrders(false)}
									onKeyDown={e => {
										if (e.key === 'Enter' || e.key === ' ') {
											setShowTodayOrders(false)
										}
									}}
									tabIndex={0}
									role="button"
									className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 cursor-pointer
		  ${!showTodayOrders
											? 'bg-gray-200 text-gray-500'
											: 'bg-transparent text-gray-500 hover:bg-gray-50'
										}`}
								>
									<CalendarDays className="w-4 h-4" />
									Últimos pedidos
								</div>
							</div>
						)}
				</div>
				<Sheet>
					<SheetTrigger asChild className="cursor-pointer">
						<Button
							variant="secondary"
							className="flex items-center justify-center "
						>
							<ListFilter size={24} color="gray" />
						</Button>
					</SheetTrigger>
					<SheetContent className="h-screen w-screen">
						<SheetHeader className="font-bold mb-6 text-foreground">
							Filtros
						</SheetHeader>
						<Form {...form}>
							<form onSubmit={form.handleSubmit(handleSubmit)}>
								<div className="flex-1 text-foreground">
									<OrderFilterTypeSelectorComponent
										filterType={filterType}
										onChange={handleChangeFilterType}
									/>
									{filterType === 'codigo' ? (
										<OrderFilterFormCodeComponent
											handlePressEnter={handlePressEnter}
											form={form}
										/>
									) : (
										<>
											<OrderFilterFormPeriodComponent
												handlePressEnter={handlePressEnter}
												form={form}
											/>
										</>
									)}
								</div>
								<div className="flex w-full gap-3 mt-8">
									<Button
										variant="secondary"
										className="flex-1"
										type="button"
										onClick={handleCancel}
									>
										Limpar
									</Button>
									<SheetClose>
										<Button
											className="flex-1"
											type="submit"
											autoFocus
											disabled={isSubmitting}
										>
											{isSubmitting ? 'Filtrando...' : 'Filtrar'}
										</Button>
									</SheetClose>
								</div>
							</form>
						</Form>
					</SheetContent>
				</Sheet>
			</div>
		</>
	)
}